import { Box } from '@chakra-ui/react';
import { Checkbox, Table } from '@morressier/alembic';
import { Fragment } from 'react';
import { ArticleTypeConfigField } from '../helpers/articleTypeConfig.types';
import { isFocusIssueComponent } from '../helpers/articleTypeConfig.utils';
import FieldConfigTableRow from './FieldConfigTableRow';
import ListLoader from 'src/components/ListLoader';

type Props = {
  isLoading: boolean;
  parentRows: ArticleTypeConfigField[];
  dependentRowsByParentId: Map<string, ArticleTypeConfigField[]>;
  fieldComponentByName: Map<string, string>;
  nonDeletableRowIds: Set<string>;
  selectedIds: Set<string>;
  isAllSelected: boolean;
  canSelectAll: boolean;
  onToggleAll: (checked: boolean) => void;
  onToggleRow: (rowId: string, checked: boolean) => void;
  onEditRow: (rowId: string) => void;
  onDeleteParentRow: (rowId: string) => void;
};

const TableHeaderRow = ({
  isAllSelected,
  canSelectAll,
  onToggleAll,
  isLoading
}: {
  isAllSelected: boolean;
  canSelectAll: boolean;
  onToggleAll: (checked: boolean) => void;
  isLoading: boolean;
}) => {
  return (
    <Table.HeaderRow gap={1}>
      <Table.HeaderCell width={10} textAlign="left">
        <Checkbox
          id="select-all-fields"
          name="select-all-fields"
          ariaLabel="Select all fields"
          isChecked={isAllSelected}
          onChange={event => onToggleAll(event.target.checked)}
          disabled={!canSelectAll || isLoading}
        >
          {' '}
        </Checkbox>
      </Table.HeaderCell>
      <Table.HeaderCell flex="1" textAlign="left">
        Field Name
      </Table.HeaderCell>
      <Table.HeaderCell flex="1" textAlign="left">
        Type
      </Table.HeaderCell>
      <Table.HeaderCell flex="1" textAlign="left">
        Requirement
      </Table.HeaderCell>
      <Table.HeaderCell flex="1" textAlign="left">
        Validations
      </Table.HeaderCell>
      <Table.HeaderCell flex="1" textAlign="left">
        Labels
      </Table.HeaderCell>
      <Table.HeaderCell width={24} textAlign="center">
        Action
      </Table.HeaderCell>
    </Table.HeaderRow>
  );
};

const FieldConfigTable: React.FC<Props> = ({
  isLoading,
  parentRows,
  dependentRowsByParentId,
  fieldComponentByName,
  nonDeletableRowIds,
  selectedIds,
  isAllSelected,
  canSelectAll,
  onToggleAll,
  onToggleRow,
  onEditRow,
  onDeleteParentRow
}) => (
  <Box width="100%" overflowX="auto">
    <Box minWidth="700px">
    <Table>
      <TableHeaderRow
        isAllSelected={isAllSelected}
        canSelectAll={canSelectAll}
        onToggleAll={onToggleAll}
        isLoading={isLoading}
      />
      <Table.Body>
        {isLoading ? (
          <ListLoader />
        ) : parentRows.length ? (
          parentRows
            .filter(parentRow => {
              if (parentRow.field === 'ArticleType') {
                return false;
              }

              const componentName = fieldComponentByName.get(
                parentRow.field.trim().toLowerCase()
              );

              return !isFocusIssueComponent(componentName);
            })
            .map(parentRow => {
              const dependentRows =
                dependentRowsByParentId.get(parentRow.id) ?? [];
              const isDeleteDisabled = nonDeletableRowIds.has(parentRow.id);

            return (
              <Fragment key={parentRow.id}>
                <FieldConfigTableRow
                  row={parentRow}
                  componentName={fieldComponentByName.get(
                    parentRow.field.trim().toLowerCase()
                  )}
                  showSelectCheckbox
                  isSelected={selectedIds.has(parentRow.id)}
                  isDeleteDisabled={isDeleteDisabled}
                  hideBottomBorder={dependentRows.length > 0}
                  onToggleRow={checked => onToggleRow(parentRow.id, checked)}
                  onEdit={() => onEditRow(parentRow.id)}
                  onDelete={() => onDeleteParentRow(parentRow.id)}
                />

                {dependentRows.map(dependentRow => (
                  <FieldConfigTableRow
                    key={dependentRow.id}
                    row={dependentRow}
                    componentName={fieldComponentByName.get(
                      dependentRow.field.trim().toLowerCase()
                    )}
                    showSelectCheckbox={false}
                    hideBottomBorder={Boolean(dependentRow?.id)}
                    isSelected={false}
                    onEdit={() => onEditRow(dependentRow.id)}
                  />
                ))}
              </Fragment>
            );
          })
        ) : (
          <Table.Row>
            <Table.RowCell flex="1" textStyle="headingItem" textAlign="center">
              No field data available for this schema.
            </Table.RowCell>
          </Table.Row>
        )}
      </Table.Body>
    </Table>
    </Box>
  </Box>
);

export default FieldConfigTable;
